import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ORBI_TOAST_SCOPE } from '@patient-ui/shared/constants';
import { ToastService } from '@patient-ui/shared-ui/utils';

import { OrbiHistoryActions } from './orbi-history.actions';
import { ORBI_HISTORY_PAGE_SIZE } from './orbi-history.reducer';
import { selectOrbiHistoryConversations } from './orbi-history.selectors';
import { OrbiError } from '../orbi/orbi-error';
import { OrbiService } from '../orbi/orbi.service';

/**
 * Conversation-history effects (PCSA-13169). Wraps the existing `OrbiService`
 * history endpoints (built in 13163). `loadOrbiHistory` resets to page 1;
 * `loadMoreOrbiHistory` appends the next page using the current list length as
 * the skip offset. End-of-list is inferred client-side (a page shorter than
 * `ORBI_HISTORY_PAGE_SIZE`) since the backend exposes no total — mirrors mobile.
 */
@Injectable()
export class OrbiHistoryEffects {
  constructor(
    private actions$: Actions,
    private orbiService: OrbiService,
    private store: Store,
    private toastService: ToastService
  ) {}

  loadOrbiHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrbiHistoryActions.loadOrbiHistory),
      exhaustMap(() =>
        this.orbiService.fetchHistory(0, ORBI_HISTORY_PAGE_SIZE).pipe(
          map((conversations) =>
            OrbiHistoryActions.loadOrbiHistorySuccess({
              conversations,
              reset: true,
              reachedEnd: conversations.length < ORBI_HISTORY_PAGE_SIZE,
            })
          ),
          catchError((error: OrbiError) =>
            of(OrbiHistoryActions.loadOrbiHistoryFailure({ error }))
          )
        )
      )
    )
  );

  loadMoreOrbiHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrbiHistoryActions.loadMoreOrbiHistory),
      withLatestFrom(this.store.select(selectOrbiHistoryConversations)),
      // exhaustMap: ignore further "load more" while a page is in flight.
      exhaustMap(([, loaded]) =>
        this.orbiService
          .fetchHistory(loaded.length, ORBI_HISTORY_PAGE_SIZE)
          .pipe(
            map((conversations) =>
              OrbiHistoryActions.loadOrbiHistorySuccess({
                conversations,
                reset: false,
                reachedEnd: conversations.length < ORBI_HISTORY_PAGE_SIZE,
              })
            ),
            catchError((error: OrbiError) =>
              of(OrbiHistoryActions.loadOrbiHistoryFailure({ error }))
            )
          )
      )
    )
  );

  renameOrbiConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrbiHistoryActions.renameOrbiConversation),
      exhaustMap(({ sessionId, title }) =>
        this.orbiService.renameConversation(sessionId, title).pipe(
          map(() =>
            OrbiHistoryActions.renameOrbiConversationSuccess({
              sessionId,
              title,
            })
          ),
          catchError((error: OrbiError) =>
            of(OrbiHistoryActions.renameOrbiConversationFailure({ error }))
          )
        )
      )
    )
  );

  deleteOrbiConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrbiHistoryActions.deleteOrbiConversation),
      exhaustMap(({ sessionId }) =>
        this.orbiService.deleteConversation(sessionId).pipe(
          map(() =>
            OrbiHistoryActions.deleteOrbiConversationSuccess({ sessionId })
          ),
          catchError((error: OrbiError) =>
            of(OrbiHistoryActions.deleteOrbiConversationFailure({ error }))
          )
        )
      )
    )
  );

  /**
   * Success toasts (PCSA-13169 AC8j / delete flow; parity with mobile
   * `ToastHelpers.showSuccessToast`). Non-dispatching — the reducer already
   * updated the list; this only surfaces the confirmation.
   */
  renameSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrbiHistoryActions.renameOrbiConversationSuccess),
        tap(({ title }) =>
          this.toastService.show(`Conversation renamed to "${title}"`, {
            classname: 'border-success-dark',
            icon: 'check_circle',
            scope: ORBI_TOAST_SCOPE,
          })
        )
      ),
    { dispatch: false }
  );

  deleteSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrbiHistoryActions.deleteOrbiConversationSuccess),
        tap(() =>
          this.toastService.show('Conversation deleted', {
            classname: 'border-success-dark',
            icon: 'check_circle',
            scope: ORBI_TOAST_SCOPE,
          })
        )
      ),
    { dispatch: false }
  );
}
